export type ClientWorkspaceSection = {
  id: string;
  navLabel: string;
  routeSegment: string;
  title: string;
  description: string;
  tone: 'primary' | 'success' | 'warning' | 'info';
  availableNow: string[];
  actionLocation: {
    label: string;
    route: string;
    detail: string;
  };
  unavailableHere: string[];
};

export const clientWorkspaceSections: ClientWorkspaceSection[] = [
  {
    id: 'overview',
    navLabel: 'Overview',
    routeSegment: 'overview',
    title: 'Client Overview',
    description: 'This route shows the selected client, its current status, and where operators need to go for client administration today.',
    tone: 'primary',
    availableNow: [
      'Confirm the selected client name, slug, and status before making changes elsewhere.',
      'Navigate between Overview, Info, Users, and Roles without losing selected-client context.',
      'Use the status badges here as a quick cross-check against the live admin client registry.',
    ],
    actionLocation: {
      label: 'Admin > Clients',
      route: '/admin/clients',
      detail: 'Use the central client admin workspace for edits, membership changes, and role work.',
    },
    unavailableHere: [
      'No company profile fields can be edited from this overview route.',
      'No user invites, membership updates, or role changes can be submitted here.',
    ],
  },
  {
    id: 'info',
    navLabel: 'Info',
    routeSegment: 'info',
    title: 'Client Information',
    description: 'This route identifies the client whose profile data is being reviewed and points operators to the live company information editor.',
    tone: 'info',
    availableNow: [
      'Review the selected client identity before opening the editable company record.',
      'Use this route as a stable slug-based reference when sharing a client-specific link.',
      'Confirm whether the client is active, onboarding, or inactive before updating profile details.',
    ],
    actionLocation: {
      label: 'Admin > Clients > Client Info',
      route: '/admin/clients',
      detail: 'Client profile, contact, branding, and address edits are handled in the Company Info tab.',
    },
    unavailableHere: [
      'No profile, support, branding, or address fields can be saved from this route.',
      'No onboarding metadata is editable here.',
    ],
  },
  {
    id: 'users',
    navLabel: 'Users',
    routeSegment: 'users',
    title: 'Client Users',
    description: 'This route keeps the selected client in view while directing operators to the live user membership and invite tools.',
    tone: 'success',
    availableNow: [
      'Confirm the client context before opening the live user membership table.',
      'Use the slug-based route to keep a direct link to the client user area.',
      'Cross-check the client status here before sending invites or changing access elsewhere.',
    ],
    actionLocation: {
      label: 'Admin > Clients > Users',
      route: '/admin/clients',
      detail: 'User invites, membership status changes, password resets, and department assignments are handled in the Users tab.',
    },
    unavailableHere: [
      'No invites can be sent from this route.',
      'No membership, password, or group-assignment changes can be saved here.',
    ],
  },
  {
    id: 'roles',
    navLabel: 'Roles',
    routeSegment: 'roles',
    title: 'Client Roles',
    description: 'This route preserves the selected client context and points operators to the live role and permission assignments for that client.',
    tone: 'warning',
    availableNow: [
      'Confirm which client you are working on before reviewing scoped permissions.',
      'Use this route as a stable client-specific entry point for role review.',
      'Keep client context visible while switching between user and role administration.',
    ],
    actionLocation: {
      label: 'Admin > Clients > Roles / Permissions',
      route: '/admin/clients',
      detail: 'Client roles, scheduler assignments, and group-scoped permissions are managed in the Roles / Permissions tab.',
    },
    unavailableHere: [
      'No company-role or group-role assignments can be changed from this route.',
      'No permission catalog editing or audit actions are available here.',
    ],
  },
];
